import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  Box,
  Avatar,
  Divider,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import MovieFilterRoundedIcon from "@mui/icons-material/MovieFilterRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PodcastsRoundedIcon from "@mui/icons-material/PodcastsRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import HistoryEduRoundedIcon from "@mui/icons-material/HistoryEduRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import useLanguage from "../../hooks/useLanguage";
import useMemorize from "../../hooks/useMemorize";
import useUserData from "../../hooks/useUserData";
import useContent from "../../hooks/useContent";
import useHighlights from "../../hooks/useHighlights";
import useTrendScout from "../../hooks/useTrendScout";
import useTodayBefore from "../../hooks/useTodayBefore";
import CommonPage from "../../components/common-page";
import { DashGrid, CardCell } from "./style";
import DashboardSkeleton from "./skeleton";
import Flag, { CountryTip } from "../trendscout/flag";
import { heatColor, topicColor } from "../trendscout/topics";
import { HeatBadge } from "../trendscout/style";
import {
  buildActivity,
  todayRecordings,
  passedTrends,
  articlePath,
  trendPath,
  formatTimeIsrael,
  timeAgo,
} from "./utils";

const navigate = (to) => {
  window.history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

const greetingKey = (h = new Date().getHours()) =>
  h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";

const REC_COLOR = {
  planned: "info",
  recording: "error",
  captured: "success",
  failed: "error",
};

function SectionCard({ i, span, icon, color, title, onViewAll, viewAllLabel, children }) {
  return (
    <CardCell data-span={span} style={{ "--i": i }}>
      <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
        <CardHeader
          avatar={
            <Avatar variant="rounded" sx={{ bgcolor: alpha(color, 0.16), color }}>
              {icon}
            </Avatar>
          }
          title={<Typography variant="h6">{title}</Typography>}
          action={
            onViewAll && (
              <Button
                size="small"
                onClick={onViewAll}
                endIcon={<ChevronRightRoundedIcon />}
                sx={{ mt: 0.5 }}
              >
                {viewAllLabel}
              </Button>
            )
          }
        />
        <Divider />
        <CardContent sx={{ pt: 1 }}>{children}</CardContent>
      </Card>
    </CardCell>
  );
}

function Rows({ items, empty, render, dir }) {
  if (!items?.length) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 3, textAlign: "center" }}
      >
        {empty}
      </Typography>
    );
  }
  const isRtl = dir === "rtl";
  return (
    <List disablePadding>
      {items.map((item, idx) => {
        const r = render(item, idx);
        const Wrapper = r.onClick ? ListItemButton : ListItem;
        return (
          <Wrapper
            key={r.key}
            onClick={r.onClick}
            sx={{
              borderRadius: 1,
              py: 0.5,
              flexDirection: isRtl ? "row-reverse" : "row",
            }}
          >
            {r.leading && (
              <Box sx={{ marginInline: 1, flexShrink: 0, display: "flex" }}>
                {r.leading}
              </Box>
            )}
            <ListItemText
              primary={r.primary}
              secondary={r.secondary}
              sx={{ textAlign: isRtl ? "right" : "left", m: 0 }}
              primaryTypographyProps={{ noWrap: true, variant: "body2", fontWeight: 500, dir }}
              secondaryTypographyProps={{ noWrap: true, variant: "caption", dir }}
            />
            {r.trailing && <Box sx={{ mx: 1, flexShrink: 0 }}>{r.trailing}</Box>}
          </Wrapper>
        );
      })}
    </List>
  );
}

export default function Home() {
  const [
    {
      pages: { home: t, trendscout: ts },
    },
  ] = useLanguage();
  const { name } = useUserData();
  const mode = useTheme().palette.mode;
  const { listContent, getRecordingPlan } = useContent();
  const { listHighlights } = useHighlights();
  const { listItems } = useTrendScout();
  const { listItems: listTodayBefore } = useTodayBefore();
  const [data, setData] = useState(null);
  const [rtlMemo, setRtlMemo] = useMemorize("home-rtl");
  const rtl = rtlMemo !== "0";
  const dir = rtl ? "rtl" : "ltr";

  useEffect(() => {
    let alive = true;
    (async () => {
      const [c, h, tr, p, tb] = await Promise.allSettled([
        listContent({ page: 1, pageSize: 6 }),
        listHighlights({ tab: "pending", pageSize: 6 }),
        listItems({ pageSize: 20 }),
        getRecordingPlan(1),
        listTodayBefore(),
      ]);
      if (!alive) return;
      const val = (r) => (r.status === "fulfilled" ? r.value : null);
      const today = val(tb);
      setData({
        articles: val(c)?.items || [],
        highlights: val(h)?.items || [],
        trends: passedTrends(val(tr)?.items || []),
        plan: val(p)?.plan || [],
        todayBefore: (today?.items || []).slice(0, 6),
        todayYear: Number(today?.dateKey?.slice(0, 4)) || new Date().getFullYear(),
      });
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const greeting = `${t.greeting[greetingKey()]}, ${name}`;
  const rtlToggle = (
    <FormControlLabel
      key="rtl"
      sx={{ mr: 0 }}
      control={
        <Switch
          size="small"
          checked={rtl}
          onChange={() => setRtlMemo(rtl ? "0" : "1")}
        />
      }
      label={
        <Typography variant="caption" color="text.secondary">
          {t.rtlToggle}
        </Typography>
      }
    />
  );

  if (!data) {
    return (
      <CommonPage title={greeting} subtitle={t.subtitle} actions={[rtlToggle]}>
        <DashboardSkeleton />
      </CommonPage>
    );
  }

  const recordings = todayRecordings(data.plan);
  const activity = buildActivity({
    articles: data.articles,
    highlights: data.highlights,
    trends: data.trends,
    plan: data.plan,
  });
  const agoLabel = (ts) => {
    const a = timeAgo(ts);
    return a.unit === "now" ? t.time.now : `${a.n}${t.time[a.unit]}`;
  };

  return (
    <CommonPage title={greeting} subtitle={t.subtitle} actions={[rtlToggle]}>
      <DashGrid>
        <SectionCard
          i={0}
          icon={<HistoryEduRoundedIcon />}
          color="#A9744F"
          title={t.todaybefore.title}
          onViewAll={() => navigate("/today-before")}
          viewAllLabel={t.viewAll}
        >
          <Rows
            dir={dir}
            items={data.todayBefore}
            empty={t.todaybefore.empty}
            render={(e) => ({
              key: e.id,
              primary: e.hebrewText,
              secondary: t.todaybefore.yearsAgo.replace(
                "{n}",
                data.todayYear - e.year
              ),
              // The only dashboard card carrying imagery — that's the pull. The
              // year fills in when an event has no picture, so no empty slots.
              leading: (
                <Avatar
                  variant="rounded"
                  src={e.imageUrl}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "background.secondary",
                    color: "text.secondary",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
                    {e.year}
                  </span>
                </Avatar>
              ),
              onClick: () => navigate("/today-before"),
            })}
          />
        </SectionCard>

        <SectionCard
          i={1}
          icon={<TrendingUpRoundedIcon />}
          color="#E16846"
          title={t.trendscout.title}
          onViewAll={() => navigate("/trendscout")}
          viewAllLabel={t.viewAll}
        >
          <Rows
            dir={dir}
            items={data.trends}
            empty={t.trendscout.empty}
            render={(tr) => {
              const heat = typeof tr.score === "number" ? heatColor(tr.score, mode) : null;
              return {
                key: tr.guid,
                primary: tr.hebrewTitle || tr.originalTitle,
                secondary: (
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    <CountryTip code={tr.country}>
                      <Flag code={tr.country} w={14} h={10} />
                    </CountryTip>
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: topicColor(tr.topic, mode).fg }}
                    >
                      {ts.topicNames[tr.topic] || tr.topic}
                    </Box>
                    {tr.sourceName ? `· ${tr.sourceName}` : ""}
                  </Box>
                ),
                // Same heat badge as the TrendScout card, so the number means
                // the same thing in both places.
                leading: heat && (
                  <HeatBadge $bg={heat.bg} $fg={heat.fg}>
                    <LocalFireDepartmentRoundedIcon sx={{ fontSize: 12 }} />
                    {tr.score}
                  </HeatBadge>
                ),
                // Land on the story itself. No topic filter: this list and the
                // All tab share one ranking, so the card is on page 1 and the
                // page rings it rather than making you hunt.
                onClick: () => navigate(trendPath(tr)),
              };
            }}
          />
        </SectionCard>

        <SectionCard
          i={2}
          icon={<ArticleRoundedIcon />}
          color="#46A0E1"
          title={t.articles.title}
          onViewAll={() => navigate("/content")}
          viewAllLabel={t.viewAll}
        >
          <Rows
            dir={dir}
            items={data.articles}
            empty={t.articles.empty}
            render={(a) => ({
              key: a.id,
              primary: a.title,
              secondary: a.showTitle || t.activity.article,
              onClick: () => navigate(articlePath(a)),
            })}
          />
        </SectionCard>

        <SectionCard
          i={3}
          icon={<PodcastsRoundedIcon />}
          color="#E1A946"
          title={t.recordings.title}
          onViewAll={() => navigate("/stream")}
          viewAllLabel={t.viewAll}
        >
          <Rows
            dir={dir}
            items={recordings}
            empty={t.recordings.empty}
            render={(r, idx) => ({
              key: idx,
              primary: r.title,
              secondary: formatTimeIsrael(r.startUtcMs),
              trailing: (
                <Chip
                  size="small"
                  variant="outlined"
                  color={REC_COLOR[r.status] || "default"}
                  label={t.recordings[r.status] || r.status}
                />
              ),
            })}
          />
        </SectionCard>

        <SectionCard
          i={4}
          icon={<MovieFilterRoundedIcon />}
          color="#46E19B"
          title={t.highlights.title}
          onViewAll={() => navigate("/highlights")}
          viewAllLabel={t.viewAll}
        >
          <Rows
            dir={dir}
            items={data.highlights}
            empty={t.highlights.empty}
            render={(h) => ({
              key: h.id,
              primary: h.generated_title || h.title,
              secondary: h.show_title,
              onClick: () => navigate("/highlights"),
            })}
          />
        </SectionCard>


        <SectionCard
          i={5}
          icon={<HistoryRoundedIcon />}
          color="#9B6BDF"
          title={t.activity.title}
        >
          <Rows
            dir={dir}
            items={activity}
            empty={t.activity.empty}
            render={(x) => ({
              key: `${x.kind}-${x.ts}-${x.title}`,
              primary: x.title,
              secondary: t.activity[x.kind],
              trailing: (
                <Typography variant="caption" color="text.secondary">
                  {agoLabel(x.ts)}
                </Typography>
              ),
              onClick: () => navigate(x.to),
            })}
          />
        </SectionCard>
      </DashGrid>
    </CommonPage>
  );
}
